import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { queryKeys } from '../api/query-keys'
import { Button, Checkbox, FieldHelp, FieldLabel, Icon, Input, Notice } from '@speaking-coach/ui'
import {
  OPERATOR_FIELD_GROUP,
  OPERATOR_FIELDSET_LEGEND,
} from '../common/form-layout'
import { PendingSpinner } from '../common/save-button'
import { adminCohortClient } from '../cohorts/api'
import type { CohortRow } from '../cohorts/types'

const SEARCH_DEBOUNCE_MS = 250
/**
 * The available list fetches a wide window and shows the first few. The
 * window is wide because already-assigned cohorts are dropped client-side
 * — with 20 fetched and 15 assigned, the operator would see five and be
 * told to search. 100 keeps that honest without a server-side exclusion.
 */
const AVAILABLE_WINDOW = 100
const AVAILABLE_SHOWN = 20

/**
 * How many selected-cohort ids to resolve per request. Each id costs
 * ~45 characters of query string (`cohortId=<uuid>`), so 50 keeps a batch
 * near 2 KB — comfortably inside every proxy's header limit, with the
 * server's own 200-row ceiling never in play.
 */
const SELECTED_BATCH = 50

/** Page size for "Add all current cohorts" — the server's ceiling. */
const ALL_CURRENT_PAGE = 200

/**
 * Above this many selected cohorts the list gets a filter box. Below it,
 * every row is on screen inside the bounded list and a filter would be
 * one more control than the task needs.
 */
const FILTER_FROM = 8

function cohortWindow(cohort: CohortRow): string {
  const starts = cohort.scheduledStartOn ?? 'on activation'
  return `Starts ${starts} · Ends ${cohort.accessEndsOn}`
}

export function CohortAccessFields({
  clientId,
  clientName,
  selected,
  onChange,
  allCohorts,
  onAllCohortsChange,
  onBusyChange,
}: {
  clientId: string
  clientName?: string
  selected: string[]
  onChange: (ids: string[]) => void
  /**
   * PROV-HRALL: while on, the server owns the set (every live cohort of the
   * client, new ones included) and refuses individual removal with 409. The
   * picker therefore does not render the search or the remove actions —
   * never offer a control already known to be refused.
   */
  allCohorts: boolean
  onAllCohortsChange: (value: boolean) => void
  /**
   * True while "Add all current cohorts" is fetching. The owning form
   * disables its submit for the duration: a create/save that raced the
   * fetch would send the selection from BEFORE the add.
   */
  onBusyChange?: (busy: boolean) => void
}): JSX.Element {
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [known, setKnown] = useState<Record<string, CohortRow>>({})
  const [filter, setFilter] = useState('')
  const [bulk, setBulk] = useState<
    { state: 'idle' } | { state: 'adding' } | { state: 'done'; message: string } | { state: 'error' }
  >({ state: 'idle' })
  const selectedSet = useMemo(() => new Set(selected), [selected])
  // The bulk add completes against the selection as it is THEN, not as it
  // was when clicked — the form may have reset or the flag flipped meanwhile.
  const selectedRef = useRef(selected)
  selectedRef.current = selected
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  const busy = bulk.state === 'adding'

  useEffect(() => {
    const handle = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  // Resolving the SELECTED cohorts' labels used to be one request asking
  // for `limit: 200` — the server's maximum — with every selected id in the
  // query string. That failed in both directions at once past 200 links:
  // the RESPONSE silently capped, so rows vanished from "Selected cohorts"
  // while the counter above still reported the full number (the
  // "Loading N linked cohorts…" line could not cover it, being gated on
  // `isPending`, and the query had succeeded); and the REQUEST did not cap
  // at all, so 400 ids sent ~18 KB of query string to be told about 200.
  //
  // Chunking fixes both without a disclosure the operator would have no
  // action for: every id is asked about, in batches whose query string is
  // bounded, and TanStack caches each batch on its own key.
  const orderedSelection = useMemo(() => [...selected].sort(), [selected])
  const idBatches = useMemo(() => {
    const batches: string[][] = []
    for (let at = 0; at < orderedSelection.length; at += SELECTED_BATCH) {
      batches.push(orderedSelection.slice(at, at + SELECTED_BATCH))
    }
    return batches
  }, [orderedSelection])

  // `combine` rather than merging the results by hand: TanStack memoizes
  // its output, so `selectedQuery.data` keeps a stable identity between
  // renders. Merging inline produced a NEW object every render, and the
  // effect below that folds rows into `known` depends on it — a render
  // loop that hung the test runner rather than failing it.
  const selectedQuery = useQueries({
    queries: idBatches.map((cohortIds) => {
      const params = { clientId, cohortIds, limit: SELECTED_BATCH, offset: 0 }
      return {
        queryKey: queryKeys.cohortList(params),
        queryFn: () => adminCohortClient.listAll(params),
        // The list is hidden while all-cohort access is on; its labels are
        // not worth a request until the operator turns it off.
        enabled: !allCohorts,
      }
    }),
    combine: (results) => ({
      // Undefined until EVERY batch has answered, so a half-resolved
      // selection never renders as a complete one.
      data: results.some((r) => r.data === undefined)
        ? undefined
        : { cohorts: results.flatMap((r) => r.data?.cohorts ?? []) },
      isPending: results.some((r) => r.isPending),
      isError: results.some((r) => r.isError),
    }),
  })
  // `total` doubles as the count the all-cohorts notice states, so this
  // query stays on in both modes.
  const availabilityParams = { clientId, limit: 1, offset: 0 }
  const availabilityQuery = useQuery({
    queryKey: queryKeys.cohortList(availabilityParams),
    queryFn: () => adminCohortClient.listAll(availabilityParams),
  })
  // Shown BEFORE the operator types anything: a picker that is blank until
  // searched presumes they know the cohort names. The search box then
  // narrows this same list.
  const searchParams = {
    clientId,
    search: search || undefined,
    limit: AVAILABLE_WINDOW,
    offset: 0,
  }
  const suggestionsQuery = useQuery({
    queryKey: queryKeys.cohortList(searchParams),
    queryFn: () => adminCohortClient.listAll(searchParams),
    enabled: !allCohorts,
    // Safe here, unlike `cohorts/cohort-picker.tsx`: `clientId` comes from
    // the route, so a change of client is a remount and there is no
    // previous data to carry. If this ever takes a client that changes
    // while mounted, scope the placeholder to the previous query's
    // `clientId` the way the picker does — otherwise the old client's rows
    // stay clickable under the new one.
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    const rows = [
      ...(selectedQuery.data?.cohorts ?? []),
      ...(suggestionsQuery.data?.cohorts ?? []),
    ]
    if (rows.length === 0) return
    setKnown((current) => {
      const next = { ...current }
      for (const cohort of rows) next[cohort.cohortId] = cohort
      return next
    })
  }, [selectedQuery.data, suggestionsQuery.data])

  const selectedCohorts = selected
    .map((cohortId) => known[cohortId])
    .filter((cohort): cohort is CohortRow => cohort !== undefined)
  const unresolvedSelected = selected.length - selectedCohorts.length
  const matching = (suggestionsQuery.data?.cohorts ?? []).filter(
    (cohort) => !selectedSet.has(cohort.cohortId),
  )
  const availableShown = matching.slice(0, AVAILABLE_SHOWN)
  const windowTruncated = (suggestionsQuery.data?.total ?? 0) > AVAILABLE_WINDOW
  const clientLabel = clientName || 'this client'
  const cohortTotal = availabilityQuery.data?.total
  // Nothing left to add: the search and the bulk action would only ever
  // answer "no available cohorts", so they are disabled WITH the reason —
  // removing one cohort is the operator's next step and re-enables them.
  const allSelected = cohortTotal !== undefined && cohortTotal > 0 && selected.length >= cohortTotal
  const filterNeedle = filter.trim().toLowerCase()
  const shownCohorts =
    filterNeedle === ''
      ? selectedCohorts
      : selectedCohorts.filter((cohort) => cohort.name.toLowerCase().includes(filterNeedle))

  function add(cohort: CohortRow): void {
    setKnown((current) => ({ ...current, [cohort.cohortId]: cohort }))
    onChange([...selected, cohort.cohortId])
    setQuery('')
    setSearch('')
  }

  /**
   * One-shot snapshot: every cohort that exists today joins the selection
   * as ordinary, individually removable grants — cohorts created later are
   * NOT included (that is what the All-cohort access flag is for). It only
   * edits the form; the operator still saves through the usual replace-set.
   * Pages through the client's list at the server ceiling so a client with
   * more cohorts than one page still gets all of them.
   */
  async function addAllCurrent(): Promise<void> {
    setBulk({ state: 'adding' })
    onBusyChange?.(true)
    try {
      const rows: CohortRow[] = []
      let offset = 0
      for (;;) {
        const page = await adminCohortClient.listAll({
          clientId,
          limit: ALL_CURRENT_PAGE,
          offset,
        })
        rows.push(...page.cohorts)
        offset += page.cohorts.length
        if (page.cohorts.length === 0 || offset >= page.total) break
      }
      setKnown((current) => {
        const next = { ...current }
        for (const cohort of rows) next[cohort.cohortId] = cohort
        return next
      })
      if (!mountedRef.current) return
      const current = selectedRef.current
      const currentSet = new Set(current)
      const missing = rows.filter((cohort) => !currentSet.has(cohort.cohortId))
      if (missing.length === 0) {
        setBulk({
          state: 'done',
          message: 'Nothing to add — every current cohort is already assigned.',
        })
        return
      }
      onChange([...current, ...missing.map((cohort) => cohort.cohortId)])
      setBulk({
        state: 'done',
        message:
          `Added ${missing.length} ${missing.length === 1 ? 'cohort' : 'cohorts'}. ` +
          'Cohorts created later are not included — save to apply.',
      })
    } catch {
      if (mountedRef.current) setBulk({ state: 'error' })
    } finally {
      onBusyChange?.(false)
    }
  }

  return (
    <fieldset className={OPERATOR_FIELD_GROUP}>
      <legend className={OPERATOR_FIELDSET_LEGEND}>
        <span className="flex items-center gap-1">
          Cohort access
          <FieldHelp label="HR cohort access">
            Add this client&rsquo;s cohorts one at a time, add every cohort that
            exists today in one step, or turn on All-cohort access so cohorts
            created later are included automatically too.
          </FieldHelp>
        </span>
      </legend>

      <div className="flex items-start gap-3 py-2">
        <Checkbox
          id="hr-all-cohort-access"
          checked={allCohorts}
          onCheckedChange={(checked) => onAllCohortsChange(checked === true)}
          className="mt-1"
        />
        <FieldLabel
          htmlFor="hr-all-cohort-access"
          label="All-cohort access"
          help="Grants every cohort of this client, including cohorts created later. While it is on, individual cohorts cannot be removed — turn it off first to choose cohorts one at a time."
        />
      </div>

      {allCohorts ? (
        <Notice variant="neutral">
          <div>
            <p className="font-medium text-text-primary">Which cohorts does this include?</p>
            <p className="mt-0.5">
              {cohortTotal === undefined
                ? `Every cohort of ${clientLabel}`
                : `All ${cohortTotal} ${cohortTotal === 1 ? 'cohort' : 'cohorts'} of ${clientLabel}`}
              , plus every cohort created later. Turn off All-cohort access to
              choose cohorts individually.
            </p>
          </div>
        </Notice>
      ) : (
        <>
          {availabilityQuery.isPending && (
            <p className="text-caption text-text-secondary">Checking available cohorts…</p>
          )}
          {availabilityQuery.isError && (
            <p role="alert" className="text-caption text-danger-text">
              Cohort availability could not be checked. Reload to try again.
            </p>
          )}
          {availabilityQuery.data?.total === 0 && (
            <p className="rounded-token-card border border-dashed border-border-default p-3 text-body text-text-secondary">
              {clientName || 'This client'} has no cohorts. Create a cohort before assigning cohort access.
            </p>
          )}
          {availabilityQuery.data && availabilityQuery.data.total > 0 && (
          <div className="relative">
            <label htmlFor="cohort-access-search" className="text-caption font-medium">
              Add cohort
            </label>
            <Input
              id="cohort-access-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={
                allSelected
                  ? 'Every current cohort is assigned'
                  : `Search ${clientLabel} cohorts by name...`
              }
              className="mt-1"
              autoComplete="off"
              disabled={allSelected}
              aria-describedby={allSelected ? 'cohort-access-all-selected' : undefined}
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                pending={bulk.state === 'adding'}
                disabled={bulk.state === 'adding' || allSelected}
                onClick={() => void addAllCurrent()}
              >
                {bulk.state === 'adding' ? <PendingSpinner surface="app" /> : <Icon name="add" />}
                {bulk.state === 'adding' ? 'Adding…' : 'Add all current cohorts'}
              </Button>
              <FieldHelp label="add all current cohorts">
                Selects every cohort {clientLabel} has today as ordinary grants you
                can still remove one by one. Cohorts created later are not included
                — turn on All-cohort access for that.
              </FieldHelp>
              {bulk.state === 'done' ? (
                // What just happened outranks the resulting state: the
                // disabled search's own placeholder already says the latter.
                <span role="status" className="text-caption text-text-secondary">
                  {bulk.message}
                </span>
              ) : bulk.state === 'error' ? (
                <span role="alert" className="text-caption text-danger-text">
                  Cohorts could not be loaded. Try again.
                </span>
              ) : allSelected ? (
                <span
                  id="cohort-access-all-selected"
                  role="status"
                  className="text-caption text-text-secondary"
                >
                  Every current cohort is assigned — remove one to search again, or
                  turn on All-cohort access to include cohorts created later.
                </span>
              ) : null}
            </div>
            {!allSelected && (
              <section
                aria-label="Available cohorts"
                tabIndex={0}
                className="mt-2 max-h-64 overflow-y-auto rounded-token-card border border-border-default bg-surface-card focus-visible:focus-ring-inset"
              >
                {suggestionsQuery.isPending && (
                  <p className="p-3 text-body text-text-secondary">Loading cohorts…</p>
                )}
                {suggestionsQuery.isError && (
                  <p role="alert" className="p-3 text-body text-danger-text">
                    Cohorts could not be loaded. Try again.
                  </p>
                )}
                {suggestionsQuery.isSuccess && matching.length === 0 && (
                  <p className="p-3 text-body text-text-secondary">
                    {search === ''
                      ? 'No available cohorts here — search by name to find others.'
                      : 'No available cohorts match that search.'}
                  </p>
                )}
                {availableShown.map((cohort) => (
                  <div
                    key={cohort.cohortId}
                    className="flex items-center justify-between gap-3 border-b border-border-default p-3 last:border-0"
                  >
                    <span className="min-w-0">
                      <span className="block text-body font-medium">{cohort.name}</span>
                      <span className="block text-caption text-text-secondary">
                        {cohortWindow(cohort)}
                      </span>
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      aria-label={`Add ${cohort.name}`}
                      disabled={busy}
                      onClick={() => add(cohort)}
                    >
                      <Icon name="add" />
                    </Button>
                  </div>
                ))}
              </section>
            )}
            {!allSelected && (matching.length > AVAILABLE_SHOWN || windowTruncated) && (
              // Below the scroll frame, not inside it: a footer that only
              // appears once the operator has scrolled to the end tells
              // nobody anything.
              <p className="mt-1 text-caption text-text-secondary">
                Showing {availableShown.length} of {matching.length}
                {windowTruncated ? '+' : ''} available — search to narrow.
              </p>
            )}
          </div>
          )}

          <section aria-label="Assigned cohorts" className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-caption font-medium text-text-secondary">
                {selected.length} assigned {selected.length === 1 ? 'cohort' : 'cohorts'}
                {filterNeedle !== '' && ` · ${shownCohorts.length} match`}
              </p>
              {selected.length > 0 && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    onChange([])
                    setFilter('')
                    setBulk({ state: 'idle' })
                  }}
                >
                  <Icon name="remove" />
                  Remove all
                </Button>
              )}
            </div>
            {(selected.length >= FILTER_FROM || filterNeedle !== '') && (
              <div>
                <label htmlFor="cohort-access-filter" className="sr-only">
                  Filter assigned cohorts
                </label>
                <Input
                  id="cohort-access-filter"
                  type="search"
                  size="sm"
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  placeholder="Filter assigned cohorts by name..."
                  autoComplete="off"
                />
              </div>
            )}
            {selectedQuery.isError && (
              <p role="alert" className="text-caption text-danger-text">
                Linked cohort details could not be loaded. Their access will be
                preserved unless you remove it after retrying.
              </p>
            )}
            {unresolvedSelected > 0 && selectedQuery.isPending && (
              <p className="text-caption text-text-secondary">
                Loading {unresolvedSelected} linked {unresolvedSelected === 1 ? 'cohort' : 'cohorts'}…
              </p>
            )}
            {selected.length === 0 ? (
              <p className="rounded-token-card border border-dashed border-border-default p-3 text-body text-text-secondary">
                No cohort access. The HR dashboard will be empty.
              </p>
            ) : (
              // Bounded: sixty selected cohorts must not push the Save button
              // four screens down. The list scrolls inside its own frame,
              // like the search suggestions above it.
              <div className="max-h-80 overflow-y-auto rounded-token-card border border-border-default bg-surface-card">
                {filterNeedle !== '' && shownCohorts.length === 0 && (
                  <p className="p-3 text-body text-text-secondary">
                    No assigned cohorts match that filter.
                  </p>
                )}
                {shownCohorts.map((cohort) => (
                  <div
                    key={cohort.cohortId}
                    className="flex items-center justify-between gap-3 border-b border-border-default px-3 py-2 last:border-0"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-body font-medium">{cohort.name}</span>
                      <span className="block text-caption text-text-secondary">
                        {cohortWindow(cohort)}
                      </span>
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      aria-label={`Remove ${cohort.name}`}
                      disabled={busy}
                      onClick={() => {
                        onChange(selected.filter((id) => id !== cohort.cohortId))
                        // The bulk result described a selection that no
                        // longer exists; a stale "Added N" would mislead.
                        setBulk({ state: 'idle' })
                      }}
                    >
                      <Icon name="remove" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </fieldset>
  )
}
